import { Badge, Separator } from '@design-system';
import type { CareScenarioSummary } from '@shared/consumerPlan';
import { CARE_ENVIRONMENT_LABELS, CARE_PHASE_COPY } from '../lib/copy';
import { hoursPerWeek } from '../lib/format';
import { ScenarioSummary } from './scenario-summary';

interface CareScenarioCardProps {
  scenario: CareScenarioSummary;
  isCustom: boolean;
}

function exampleActivities(activities: string, reason: string) {
  const example = activities.charAt(0).toLowerCase() + activities.slice(1);
  return `Often, assistance with ${example}. ${reason}`;
}

/** The projected care scenario in everyday terms: when, how long, where, and what help looks like. */
export function CareScenarioCard({ scenario, isCustom }: CareScenarioCardProps) {
  return (
    <section
      data-testid="consumer-care-scenario"
      className="flex flex-col gap-8"
    >
      <div className="flex flex-col gap-4">
        <div className="hidden flex-col gap-1 sm:flex">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-gray-950">
              {isCustom ? 'Your custom care scenario' : 'Your estimated care needs'}
            </h2>
            <Badge variant={isCustom ? 'outline' : 'secondary'}>
              {isCustom ? 'Custom scenario' : 'Our estimate'}
            </Badge>
          </div>
          <p className="text-muted-foreground text-sm">
            {isCustom
              ? 'Your adjustments, with the same start age and length of care.'
              : 'A possible care journey based on people with a similar profile.'}
          </p>
        </div>

        <ScenarioSummary scenario={scenario} />
      </div>

      <section aria-label="Typical activities as needs change">
        <p className="text-muted-foreground mb-4 text-sm">
          Examples of how needs often change, not a prediction of your exact activities.
        </p>
        <ol className="flex flex-col gap-4 sm:grid sm:grid-cols-3 sm:gap-8">
          {scenario.phases.map((phase, index) => {
            const isLast = index === scenario.phases.length - 1;
            const phaseCopy = CARE_PHASE_COPY[phase.carePhase];
            const monthsLabel = `${Math.round(phase.months)} months`;
            return (
              <li key={phase.carePhase} className="relative overflow-visible">
                {!isLast && (
                  <span
                    aria-hidden
                    className="bg-primary/40 absolute top-2 bottom-[-1rem] left-[4px] z-0 w-0.5 sm:hidden"
                  />
                )}
                <div className="relative flex h-full flex-col">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <span className="bg-primary ring-background relative z-10 size-2.5 shrink-0 rounded-full ring-2" />
                      <strong className="min-w-0 text-sm text-gray-950">{phaseCopy.title}</strong>
                      <span className="text-primary bg-primary/10 shrink-0 rounded px-1.5 py-0.5 text-xs sm:hidden">
                        {monthsLabel}
                      </span>
                      {!isLast && (
                        <span aria-hidden className="relative hidden h-0.5 min-w-2 flex-1 sm:block">
                          <span className="bg-primary/40 absolute inset-y-0 -right-[calc(2rem+5px)] left-0" />
                        </span>
                      )}
                    </div>
                    <span className="text-primary bg-primary/10 ml-[18px] hidden w-fit rounded px-1.5 py-0.5 text-xs sm:inline">
                      {monthsLabel}
                    </span>
                  </div>
                  <div className="mt-2 ml-[18px] flex flex-col">
                    <p className="text-muted-foreground text-sm whitespace-nowrap">
                      {CARE_ENVIRONMENT_LABELS[phase.careEnvironment]} ·{' '}
                      {hoursPerWeek(phase.careHoursPerWeek, 'hrs of care per week')}
                    </p>
                    <Separator className="mt-2" />
                    <p className="mt-2 text-sm text-gray-950">
                      {exampleActivities(phaseCopy.activities, phaseCopy.reason)}
                    </p>
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      </section>
    </section>
  );
}
